import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Briefcase, DollarSign, Calendar, Users } from "lucide-react";
import { useState } from "react";

export default function BrandMarketplace() {
  const [sportFilter, setSportFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const { data: opportunities, isLoading } = useQuery({
    queryKey: ["brand-opportunities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_registry")
        .select("*")
        .eq("partner_tier", "marketplace")
        .not("opportunity_posts", "is", null);

      if (error) throw error;

      // Flatten opportunities from all brands
      const allOpportunities: any[] = [];
      data.forEach((brand) => {
        const posts = brand.opportunity_posts as any[];
        if (posts && Array.isArray(posts)) {
          posts.forEach((post) => {
            allOpportunities.push({
              ...post,
              brandName: brand.primary_name,
              brandLogo: brand.logo_url,
              brandId: brand.id,
            });
          });
        }
      });

      return allOpportunities;
    },
  });

  const filteredOpportunities = opportunities?.filter((opp) => {
    const sportMatch = sportFilter === "all" || opp.sport === sportFilter;
    const categoryMatch =
      categoryFilter === "all" || opp.category === categoryFilter;
    return sportMatch && categoryMatch;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Brand Marketplace</h1>
          <p className="text-muted-foreground">
            Discover NIL opportunities from verified brands
          </p>
        </div>

        <div className="flex gap-4">
          <Select value={sportFilter} onValueChange={setSportFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by sport" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sports</SelectItem>
              <SelectItem value="Football">Football</SelectItem>
              <SelectItem value="Basketball">Basketball</SelectItem>
              <SelectItem value="Baseball">Baseball</SelectItem>
              <SelectItem value="Soccer">Soccer</SelectItem>
            </SelectContent>
          </Select>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="Social Media">Social Media</SelectItem>
              <SelectItem value="Endorsement">Endorsement</SelectItem>
              <SelectItem value="Appearance">Appearance</SelectItem>
              <SelectItem value="Autograph">Autograph</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-1/2 mt-2"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-32 bg-muted rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredOpportunities?.map((opportunity, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    {opportunity.brandLogo && (
                      <img
                        src={opportunity.brandLogo}
                        alt={opportunity.brandName}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <CardTitle className="text-lg">{opportunity.title}</CardTitle>
                      <CardDescription>{opportunity.brandName}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {opportunity.description}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">
                      <Briefcase className="h-3 w-3 mr-1" />
                      {opportunity.category}
                    </Badge>
                    <Badge variant="secondary">
                      <Users className="h-3 w-3 mr-1" />
                      {opportunity.sport}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        Compensation
                      </span>
                      <span className="font-semibold">
                        ${opportunity.compensation?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        Positions
                      </span>
                      <span className="font-semibold">
                        {opportunity.positions || 1}
                      </span>
                    </div>
                    {opportunity.deadline && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Deadline
                        </span>
                        <span className="font-semibold">
                          {new Date(opportunity.deadline).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="pt-2 border-t">
                    <h4 className="text-sm font-semibold mb-2">Deliverables:</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {opportunity.deliverables?.map(
                        (deliverable: string, idx: number) => (
                          <li key={idx}>• {deliverable}</li>
                        )
                      )}
                    </ul>
                  </div>

                  <Button className="w-full">Apply for Opportunity</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!isLoading && filteredOpportunities?.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                No opportunities found matching your criteria
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
